import { FilterItem, FilterItemPascal, KeyedArray, mapToKeyedArray, mapToKeyedArrayPascal } from "@repo/models";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import { useCallback } from "react";

import ApiRoute from "./apiRoute";
import { useTLPCloudApi } from "./tlpCloudApi";

export function useLocationPortalApi(controller = ApiRoute) {
	const TlpApi = useTLPCloudApi();
	const Get = useCallback(
		function <TOut>(action: string, params: object = {}) {
			return TlpApi.Get<TOut>(controller, action, params);
		},
		[TlpApi.Get, controller],
	);
	const Post = useCallback(
		function <TOut, TIn = any>(action: string, params: object = {}, data?: TIn, config?: AxiosRequestConfig) {
			return TlpApi.Post<TOut, TIn>(controller, action, params, data, config);
		},
		[TlpApi.Post, controller],
	);
	const Put = useCallback(
		function <TOut, TIn = any>(action: string, params: object = {}, data?: TIn, config?: AxiosRequestConfig) {
			return TlpApi.Put<TOut, TIn>(controller, action, params, data, config);
		},
		[TlpApi.Put, controller],
	);
	const Delete = useCallback(
		function <TOut>(action: string, id: number) {
			return TlpApi.Delete<TOut>(controller, action, id);
		},
		[TlpApi.Delete, controller],
	);

	return { Get, Post, Put, Delete };
}

export function useQueryWrapper<T>(key: string | string[], triggers: any[], apiFn: () => Promise<T>, options?: any) {
	const queryClient = useQueryClient();
	const keyArr = typeof key === "string" ? [key] : (key as string[]);
	if (!keyArr?.length) throw "No valid key for query";
	const query = useQuery<T>({
		queryKey: [...keyArr, ...triggers],
		queryFn: apiFn,
		...options,
	});
	const loaded = query.isFetched;
	const loading = query.isFetching;
	const clearQuery = useCallback(() => queryClient.invalidateQueries({ queryKey: [...key] }), [queryClient, ...key]);

	return { data: query.data as T, loaded, clearQuery, loading };
}

export function useFilterItemQueryWrapper<T extends FilterItemPascal = FilterItemPascal>(
	key: string[],
	triggers: any[],
	apiFn: () => Promise<T[]>,
	options?: any,
) {
	const callApi = async () => await apiFn().then(mapToKeyedArrayPascal);

	const queryClient = useQueryClient();
	const query = useQuery<KeyedArray<T>>({
		queryKey: [...key, ...triggers],
		queryFn: callApi,
		...options,
	});
	const loaded = !query.isFetching;
	const clearQuery = useCallback(() => queryClient.invalidateQueries({ queryKey: [...key] }), [queryClient, ...key]);

	return { data: query.data, loaded, clearQuery };
}

export function useFilterItemV2QueryWrapper<T extends FilterItem = FilterItem>(
	key: string[],
	triggers: any[],
	apiFn: () => Promise<(T | FilterItem)[]>,
	options?: any,
) {
	const callApi = async () => await apiFn().then(mapToKeyedArray);

	const queryClient = useQueryClient();
	const { data, isFetching } = useQuery<KeyedArray<T, number>>({
		queryKey: [...key, ...triggers],
		queryFn: callApi,
		...options,
	});
	const loaded = !isFetching;
	const clearQuery = useCallback(() => queryClient.invalidateQueries({ queryKey: [...key] }), [queryClient, ...key]);
	return {
		data: mapToKeyedArray(data?.data),
		loaded,
		clearQuery,
	};
}
